import React, { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Theme } from '../styles/theme';
import { useDispatch, useSelector } from 'react-redux';
import {
  setDisplayState,
  showNextCard,
  incrementErrorCount,
  selectMemorizeState,
  selectCurrentCardId
} from './memorize.slice';
import { CardMarkupViewer } from '../markup/cardMarkupViewer/cardMarkupViewer';
import { CardMarkupViewerState } from '../markup/cardMarkupViewer/cardMarkupViewer.interface';
import { cardsActions } from '../cards/cards.slice';
import { RootState } from '../store/rootReducer';
import { markupFormatter } from '../common';
import { ISubcard } from '../cards/multicards.interface';
import { MULTICARD_SEPARATOR } from '../review/utils/multicard.utils';

export const MemorizeCardDisplay = memo(() => {
  const dispatch = useDispatch();
  const { displayState, selectedCardIds, subcardTurn } = useSelector(selectMemorizeState);
  const currentCardId = useSelector(selectCurrentCardId);
  const cardsState = useSelector((state: RootState) => state.cards);

  // Load the current card when it changes
  useEffect(() => {
    if (currentCardId !== null && currentCardId !== undefined && typeof currentCardId === 'string' && currentCardId.length > 0 && cardsState[currentCardId] === undefined) {
      dispatch(cardsActions.loadCard(currentCardId));
    }
  }, [currentCardId, cardsState, dispatch]);

  // For multicards, show a single random subcard per turn instead of the whole
  // card. Re-pick whenever a new card is presented (currentCardId/subcardTurn)
  // or once its markup finishes loading.
  const currentMarkup =
    currentCardId !== null && currentCardId !== undefined && currentCardId !== ''
      ? cardsState[currentCardId]?.markup
      : undefined;
  const [currentSubcard, setCurrentSubcard] = useState<ISubcard | null>(null);

  useEffect(() => {
    if (currentMarkup === undefined || currentMarkup === '') {
      setCurrentSubcard(null);
      return;
    }
    const tree = markupFormatter.parse(currentMarkup);
    if (tree.hasSubCards && tree.subCards.length > 0) {
      const index = Math.floor(Math.random() * tree.subCards.length);
      setCurrentSubcard(tree.subCards[index]);
    } else {
      setCurrentSubcard(null);
    }
  }, [currentCardId, subcardTurn, currentMarkup]);

  const handleShowAnswer = () => {
    dispatch(setDisplayState('answer'));
  };
  
  const handleNext = () => {
    dispatch(showNextCard());
  };
  
  const handleMarkError = () => {
    if (currentCardId !== null && currentCardId !== undefined && currentCardId !== '') {
      dispatch(incrementErrorCount(currentCardId));
    }
    handleNext();
  };
  
  if (selectedCardIds.length === 0) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateMessage>
            Select cards from the list to start memorizing
          </EmptyStateMessage>
        </EmptyState>
      </Container>
    );
  }
  
  if (currentCardId === null || currentCardId === undefined || currentCardId === '') {
    return (
      <Container>
        <EmptyState>
          <EmptyStateMessage>
            No card selected
          </EmptyStateMessage>
        </EmptyState>
      </Container>
    );
  }
  
  const cardStore = cardsState[currentCardId];
  
  if (cardStore === null || cardStore === undefined) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateMessage>
            Loading card...
          </EmptyStateMessage>
        </EmptyState>
      </Container>
    );
  }
  
  const markup = currentSubcard
    ? (displayState === 'question'
        ? currentSubcard.question
        : `${currentSubcard.question}${MULTICARD_SEPARATOR}${currentSubcard.answer}`)
    : cardStore.markup;

  const viewerState: CardMarkupViewerState =
    displayState === 'question'
      ? CardMarkupViewerState.OnlyQuestion
      : currentSubcard !== null
        ? CardMarkupViewerState.Full
        : CardMarkupViewerState.QuestionAndAnswer;
  
  return (
    <Container>
      <CardContainer>
        <CardContent>
          {currentSubcard && currentSubcard.group !== undefined && currentSubcard.group.length > 0 && (
            <GroupBadge>{currentSubcard.group}</GroupBadge>
          )}
          <CardMarkupViewer
            cardId={currentCardId}
            markup={markup}
            state={viewerState}
            referenceOverride={
              currentSubcard
                ? markupFormatter.parse(cardStore.markup).reference
                : undefined
            }
          />
        </CardContent>
      </CardContainer>
      
      <ButtonsContainer>
        {displayState === 'question' ? (
          <Button onClick={handleShowAnswer}>
            Show Answer
          </Button>
        ) : (
          <>
            <ButtonGroup>
              <ErrorButton onClick={handleMarkError}>
                Got It Wrong
              </ErrorButton>
              <SuccessButton onClick={handleNext}>
                Got It Right
              </SuccessButton>
            </ButtonGroup>
          </>
        )}
      </ButtonsContainer>
      
      <InfoBar>
        <InfoText>
          Card {selectedCardIds.length > 0 && currentCardId !== null && currentCardId !== undefined && currentCardId !== '' ? selectedCardIds.indexOf(currentCardId) + 1 : 0} of {selectedCardIds.length}
        </InfoText>
      </InfoBar>
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  background: ${(props: { theme: Theme }) => props.theme.primaryBackground};
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyStateMessage = styled.div`
  color: ${(props: { theme: Theme }) => props.theme.secondaryText};
  font-size: 18px;
  text-align: center;
  padding: 32px;
`;

const CardContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  > div {
    width: 100%;
    max-width: 800px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const GroupBadge = styled.div`
  display: inline-block;
  align-self: flex-start;
  margin-bottom: 12px;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${(props: { theme: Theme }) => props.theme.buttonText};
  background-color: ${(props: { theme: Theme }) => props.theme.accentColor};
`;

const ButtonsContainer = styled.div`
  padding: 24px 32px;
  border-top: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  background: ${(props: { theme: Theme }) => props.theme.secondaryBackground};
  display: flex;
  justify-content: center;
  gap: 16px;
`;

const Button = styled.button`
  padding: 12px 32px;
  font-size: 16px;
  border: ${(props: { theme: Theme }) => props.theme.neutralButtonBorder} 1px solid;
  border-radius: 6px;
  background: ${(props: { theme: Theme }) => props.theme.neutralButtonBackground};
  color: ${(props: { theme: Theme }) => props.theme.neutralButtonForeground};
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.neutralButtonBorder};
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const ErrorButton = styled(Button)`
  background: ${(props: { theme: Theme }) => props.theme.dangerColor || '#dc3545'};
  color: white;
  border-color: ${(props: { theme: Theme }) => props.theme.dangerColor || '#dc3545'};
  
  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.dangerColor || '#dc3545'};
    opacity: 0.9;
  }
`;

const SuccessButton = styled(Button)`
  background: ${(props: { theme: Theme }) => props.theme.successColor || '#28a745'};
  color: white;
  border-color: ${(props: { theme: Theme }) => props.theme.successColor || '#28a745'};
  
  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.successColor || '#28a745'};
    opacity: 0.9;
  }
`;

const InfoBar = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 8px 16px;
  background: ${(props: { theme: Theme }) => props.theme.secondaryBackground};
  border-top: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  border-left: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  border-top-left-radius: 8px;
`;

const InfoText = styled.div`
  font-size: 12px;
  color: ${(props: { theme: Theme }) => props.theme.secondaryText};
`;